import {
  type Grade,
  type GradeCategory,
  type Lesson,
} from "@core/grades/types";
import { useIsLoggedIn } from "@framework/account/hooks";
import { trans } from "@mongez/localization";
import { useSearchParams } from "@remix-run/react";
import { useCallback, useEffect, useMemo, useState } from "react";

type LessonsCatalogFilters = {
  search: string;
  grade: string;
  category: string;
};

type ActiveFilter = {
  key: keyof LessonsCatalogFilters;
  label: string;
};

function uniqueById<T extends { id: number }>(items: T[]) {
  return items.filter(
    (item, index) => items.findIndex(({ id }) => id === item.id) === index,
  );
}

export function useLessonsCatalogPage(lessons: Lesson[]) {
  const isLoggedIn = useIsLoggedIn();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || "",
  );
  const selectedGrade = searchParams.get("grade") || "";
  const selectedCategory = searchParams.get("category") || "";

  useEffect(() => {
    const urlSearchTerm = searchParams.get("search") || "";
    setSearchTerm((currentTerm) =>
      currentTerm === urlSearchTerm ? currentTerm : urlSearchTerm,
    );
  }, [searchParams]);

  const grades = useMemo(
    () =>
      uniqueById(
        lessons.map((lesson) => lesson.grade).filter(Boolean) as Grade[],
      ),
    [lessons],
  );

  const categories = useMemo(
    () =>
      uniqueById(
        lessons
          .map((lesson) => lesson.category)
          .filter(Boolean) as GradeCategory[],
      ),
    [lessons],
  );

  const lessonsByCategory = useMemo(() => {
    const groupedLessons = new Map<
      string,
      { category?: GradeCategory; lessons: Lesson[] }
    >();

    lessons.forEach((lesson) => {
      const categoryName =
        lesson.category?.name || trans("grades.uncategorized");
      const currentGroup = groupedLessons.get(categoryName);

      if (currentGroup) {
        currentGroup.lessons.push(lesson);
        return;
      }

      groupedLessons.set(categoryName, {
        category: lesson.category,
        lessons: [lesson],
      });
    });

    return Array.from(groupedLessons.entries())
      .map(([name, group]) => ({ name, ...group }))
      .sort((first, second) => second.lessons.length - first.lessons.length);
  }, [lessons]);

  const updateSearchParams = useCallback(
    (updates: Partial<LessonsCatalogFilters>) => {
      const nextParams = new URLSearchParams(searchParams);

      Object.entries(updates).forEach(([key, value]) => {
        if (value && value !== "all") {
          nextParams.set(key, value);
        } else {
          nextParams.delete(key);
        }
      });

      nextParams.set("page", "1");
      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams],
  );

  const submitSearch = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      updateSearchParams({ search: searchTerm.trim() });
    },
    [searchTerm, updateSearchParams],
  );

  const clearSearch = useCallback(() => {
    setSearchTerm("");
    updateSearchParams({ search: "" });
  }, [updateSearchParams]);

  const changeGrade = useCallback(
    (value: string) => updateSearchParams({ grade: value }),
    [updateSearchParams],
  );

  const changeCategory = useCallback(
    (value: string) => updateSearchParams({ category: value }),
    [updateSearchParams],
  );

  const removeFilter = useCallback(
    (key: keyof LessonsCatalogFilters) => {
      if (key === "search") setSearchTerm("");
      updateSearchParams({ [key]: "" });
    },
    [updateSearchParams],
  );

  const clearFilters = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("search");
    nextParams.delete("grade");
    nextParams.delete("category");
    nextParams.set("page", "1");
    setSearchTerm("");
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  const activeFilters = useMemo(() => {
    const filters: ActiveFilter[] = [];

    if (searchParams.get("search")) {
      filters.push({
        key: "search",
        label: `${trans("lessonCatalog.searchFor")}: ${searchParams.get("search")}`,
      });
    }

    if (!isLoggedIn && selectedGrade) {
      const grade = grades.find(({ id }) => id.toString() === selectedGrade);
      filters.push({
        key: "grade",
        label: grade?.name || selectedGrade,
      });
    }

    if (selectedCategory) {
      const category = categories.find(
        ({ id }) => id.toString() === selectedCategory,
      );
      filters.push({
        key: "category",
        label: category?.name || selectedCategory,
      });
    }

    return filters;
  }, [
    categories,
    grades,
    isLoggedIn,
    searchParams,
    selectedCategory,
    selectedGrade,
  ]);

  return {
    activeFilters,
    categories,
    changeCategory,
    changeGrade,
    clearFilters,
    clearSearch,
    grades,
    isLoggedIn,
    lessonsByCategory,
    removeFilter,
    searchTerm,
    selectedCategory,
    selectedGrade,
    setSearchTerm,
    submitSearch,
  };
}
